import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/lib/auth';
import { usePrefs } from '@/lib/prefs';
import { api, parseErr } from '@/lib/api';
import { RoleBadge } from '@/components/common/RoleBadge';
import { STAGE_LABEL, STAGES, canSeeWOs, normalizeStage } from '@/lib/roles';
import { MiniBars } from '@/components/common/MiniBars';
import { Link } from 'react-router-dom';
import { Layers, Bell, Inbox, Users, Sparkles, Compass, BookOpen, ArrowRight, Target, AlertTriangle, BarChart3, GraduationCap } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { useFeature } from '@/lib/features';
import { WeeklyRecapCard } from '@/components/rhythm/WeeklyRecapCard';
import { AccountsAlertPanel } from '@/components/wo/AccountsAlertPanel';
import { WelcomeNudge } from '@/components/home/WelcomeNudge';
import { useGuidedTour } from '@/components/tour/GuidedTour';

const KpiTile = ({ label, value, hint, icon: Icon, testId, accent = false }) => (
  <div data-testid={testId}
       className={`border rounded-xl p-4 flex items-start justify-between transition-all card-hover ${accent ? '' : ''}`}
       style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--card))' }}>
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
      <div className="font-heading text-3xl mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">{hint}</div>}
    </div>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-none"
         style={{ background: accent ? 'hsl(var(--accent))' : 'hsl(var(--surface-2))',
                  color: accent ? 'hsl(var(--accent-foreground))' : 'hsl(var(--foreground-muted))' }}>
      <Icon size={18} />
    </div>
  </div>
);

const greetingFor = (hour) => {
  if (hour < 5) return 'Working late';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Working late';
};

const dateLine = () => {
  return new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
};

export default function HomePage() {
  const { user } = useAuth();
  const { prefs } = usePrefs();
  const { start: startTour } = useGuidedTour();
  const [projects, setProjects] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [wos, setWos] = useState([]);
  const [commitments, setCommitments] = useState(null);
  const [awaitingMe, setAwaitingMe] = useState({ count: 0, items: [] });
  const rhythmOn = useFeature('operating_rhythm_v4');
  const reportsOn = useFeature('reports');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, n] = await Promise.all([api.get('/projects'), api.get('/notifications')]);
        if (!alive) return;
        setProjects(p.data || []);
        setNotifs((n.data || []).slice(0, 6));
        if (canSeeWOs(user.role)) {
          const w = await api.get('/work_orders');
          if (alive) setWos(w.data || []);
        }
        if (rhythmOn) {
          try {
            const c = await api.get('/commitments/me?window=all');
            if (alive) setCommitments(c.data);
          } catch (e) { console.debug('commitments load skipped:', e?.response?.status || e); }
        }
        try {
          const a = await api.get('/projects/stage-signoffs/awaiting-me');
          if (alive) setAwaitingMe(a.data || { count: 0, items: [] });
        } catch (e) { /* non-fatal */ }
      } catch (e) { toast.error(parseErr(e)); }
    })();
    return () => { alive = false; };
  }, [user.role, rhythmOn]);

  const now = new Date();
  const greeting = greetingFor(now.getHours());
  const pendingWos = wos.filter(w => w.status !== 'activated').length;
  const firstName = user.name?.split(' ')[0] || 'there';
  const showReports = reportsOn && ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'operations', 'accounts'].includes(user.role);

  return (
    <AppShell breadcrumbs={[{ label: 'Home' }]}>
      <div className="p-6 max-w-[1320px] mx-auto fade-up" data-testid="home-page">
        <WelcomeNudge />
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-subtle))] tracking-wide">{dateLine()}</div>
            <h1 className="font-heading text-[34px] font-semibold leading-tight tracking-tight mt-1" data-testid="home-greeting">
              {greeting}, {firstName}. {prefs.status_emoji && <span className="align-middle">{prefs.status_emoji}</span>}
            </h1>
            <p className="text-[hsl(var(--foreground-muted))] text-[14.5px] mt-1">
              {prefs.status_text ? `“${prefs.status_text}” · ` : ''}
              Here’s a quiet snapshot of what matters today.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => startTour('home-cta')}
              data-testid="home-take-tour-btn"
              className="chip chip-accent hover:opacity-90"
              title="Interactive walkthrough of the studio"
            >
              <Compass size={12} /> Take a Tour
            </button>
            <RoleBadge role={user.role} />
            <Link to="/settings" className="chip chip-accent" data-testid="home-personalize-link"><Sparkles size={12} /> Personalize</Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile testId="kpi-tile-projects" label={user.role === 'team' ? 'My projects' : 'Active projects'} value={projects.length} icon={Layers} hint="open across pipeline" accent />
          <KpiTile testId="kpi-tile-notifications" label="Unread alerts" value={notifs.filter(n => !n.read).length} icon={Bell} hint="in your notification center" />
          {canSeeWOs(user.role) ? (
            <KpiTile testId="kpi-tile-work-orders" label="WOs pending verify" value={pendingWos} icon={Inbox} hint="needs AI parse + human confirm" />
          ) : (
            <KpiTile testId="kpi-tile-team" label="Team" value={user.department || '—'} icon={Users} hint="your reporting group" />
          )}
          <KpiTile testId="kpi-tile-role" label="Your role" value={user.role === 'owner' ? 'Owner' : (user.role || '').replace('_', ' ')} icon={Sparkles} hint="server-side enforced" />
        </div>

        <AtAGlance projects={projects} commitments={rhythmOn ? commitments : null} />

        {['owner', 'operations'].includes(user.role) && (
          <div className="mt-6" data-testid="home-weekly-recap">
            <WeeklyRecapCard compact />
          </div>
        )}

        <AccountsAlertPanel className="mt-6" compact />

        {awaitingMe.count > 0 && (
          <div className="mt-6 card" data-testid="home-awaiting-signoff-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold flex items-center gap-2">
                <AlertTriangle size={16} className="text-[hsl(var(--danger))]" />
                Awaiting your sign-off
                <span className="chip chip-warning" data-testid="home-awaiting-count">{awaitingMe.count}</span>
              </div>
            </div>
            <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
              {awaitingMe.items.slice(0, 6).map(it => (
                <Link key={`${it.project_id}-${it.stage}`}
                      to={`/projects/${it.project_id}`}
                      className="block px-4 py-2.5 hover:bg-[hsl(var(--surface-1))]"
                      data-testid={`home-awaiting-${it.project_id}-${it.stage}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-medium truncate">{it.project_name}</div>
                      <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] truncate">
                        {it.project_code ? <><span className="font-mono">{it.project_code}</span> · </> : null}
                        Submitted {fmtDate(it.submitted_at)}
                      </div>
                    </div>
                    <span className="chip chip-warning">{it.stage_label}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {rhythmOn && commitments && (
          <div className="mt-6 card" data-testid="home-commitments-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold flex items-center gap-2">
                <Target size={16} />My commitments
                <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))] font-normal">
                  · {commitments.counts.open} open · {commitments.counts.overdue} overdue
                </span>
              </div>
              <Link to="/commitments" className="text-[12.5px] text-[hsl(var(--primary))] underline flex items-center gap-1">
                Open <ArrowRight size={12} />
              </Link>
            </div>
            <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
              {commitments.commitments.filter(c => c.derived_status !== 'done' && c.derived_status !== 'cancelled').slice(0, 5).map(c => (
                <div key={c.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`home-commit-${c.id}`}>
                  <div className={`w-2 h-2 rounded-full flex-none ${c.derived_status === 'overdue' ? 'bg-[hsl(var(--danger))]' : 'bg-[hsl(var(--primary))]'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] truncate">{c.title}</div>
                    <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                      Target {c.target_date}
                      {c.derived_status === 'overdue' && !c.root_cause && (
                        <span className="text-[hsl(var(--danger))] ml-1">· needs root cause</span>
                      )}
                    </div>
                  </div>
                  {c.derived_status === 'overdue' && !c.root_cause && (
                    <AlertTriangle size={14} className="text-[hsl(var(--danger))] flex-none" />
                  )}
                </div>
              ))}
              {commitments.commitments.filter(c => c.derived_status !== 'done' && c.derived_status !== 'cancelled').length === 0 && (
                <div className="px-4 py-6 text-center text-[13.5px] text-[hsl(var(--foreground-muted))]">
                  No open commitments. Set some at Commitments →
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <div className="lg:col-span-2 card" data-testid="home-projects-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold">Your projects</div>
              <Link to="/projects" className="text-[12.5px] text-[hsl(var(--primary))] underline flex items-center gap-1">View all <ArrowRight size={12} /></Link>
            </div>
            <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
              {projects.length === 0 && <div className="p-6 text-[hsl(var(--foreground-muted))] text-[14px]">No projects yet.</div>}
              {projects.slice(0, 5).map(p => (
                <Link key={p.id} to={`/projects/${p.id}`} className="block px-4 py-3 hover:bg-[hsl(var(--surface-1))]" data-testid={`home-project-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5 truncate">{p.client_name} · <span className="font-mono">{p.code}</span></div>
                    </div>
                    <span className="chip chip-info">{STAGE_LABEL[normalizeStage(p.current_stage)]}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="card" data-testid="home-notifications-panel">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold">Latest alerts</div>
              <Link to="/notifications" className="text-[12.5px] text-[hsl(var(--primary))] underline flex items-center gap-1">View all <ArrowRight size={12} /></Link>
            </div>
            <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
              {notifs.length === 0 && <div className="p-4 text-[14px] text-[hsl(var(--foreground-muted))]">All caught up.</div>}
              {notifs.map(n => (
                <Link to={n.link || '/notifications'} key={n.id} className={`block px-4 py-3 hover:bg-[hsl(var(--surface-1))] ${n.read ? '' : 'bg-[hsl(var(--surface-1))]'}`}>
                  <div className="text-[13.5px] font-medium">{n.title}</div>
                  {n.body && <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] line-clamp-2">{n.body}</div>}
                  <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1">{fmtDate(n.created_at)}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className={showReports
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6'
          : 'grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6'}>
          {showReports && (
            <Link to="/reports" className="card card-hover p-4 flex items-center gap-3" data-testid="home-reports-cta">
              <div className="w-10 h-10 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--surface-2))' }}>
                <BarChart3 size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-semibold">Reports</div>
                <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">Project health, financials, workload &amp; milestones.</div>
              </div>
            </Link>
          )}
          <Link to="/wizard" className="card card-hover p-4 flex items-center gap-3" data-testid="home-wizard-cta">
            <div className="w-10 h-10 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}>
              <Compass size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold">What do I do next?</div>
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">Your responsibilities, deliverables, and next QC gate.</div>
            </div>
          </Link>
          <Link to="/knowledge" className="card card-hover p-4 flex items-center gap-3" data-testid="home-knowledge-cta">
            <div className="w-10 h-10 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--surface-2))' }}>
              <BookOpen size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold">Knowledge Hub</div>
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">SOPs A–H, templates, email library, Quick Reference.</div>
            </div>
          </Link>
          <Link to="/guide" className="card card-hover p-4 flex items-center gap-3" data-testid="home-guide-cta">
            <div className="w-10 h-10 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}>
              <GraduationCap size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold">How to use this app</div>
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">A 2-minute guide to the key flows — always here when you need it.</div>
            </div>
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

const STAGE_COLORS = [
  'hsl(var(--primary))', 'hsl(var(--info))', 'hsl(var(--warning))',
  'hsl(var(--success))', 'hsl(var(--neutral))', 'hsl(var(--foreground-subtle))',
];

function AtAGlance({ projects, commitments }) {
  const list = commitments?.commitments || [];
  const nOpen = list.filter(c => c.derived_status === 'open').length;
  const nOver = list.filter(c => c.derived_status === 'overdue').length;
  const nDone = list.filter(c => c.derived_status === 'done').length;
  const tTotal = nOpen + nOver + nDone;

  const counts = {};
  projects.forEach(p => {
    const s = normalizeStage(p.current_stage);
    counts[s] = (counts[s] || 0) + 1;
  });
  const stageItems = STAGES
    .map((s, i) => ({ label: s.label, count: counts[s.key] || 0, color: STAGE_COLORS[i % STAGE_COLORS.length] }))
    .filter(x => x.count > 0);

  if (stageItems.length === 0 && tTotal === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6" data-testid="home-at-a-glance">
      <div className="card p-4">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
          Your tasks at a glance
        </div>
        {tTotal === 0 ? (
          <div className="text-[12.5px] italic text-[hsl(var(--foreground-subtle))]">No tasks yet — add one from My Week.</div>
        ) : (
          <>
            <div className="h-2.5 rounded-full flex overflow-hidden" style={{ background: 'hsl(var(--surface-2))' }} data-testid="home-task-progress">
              {nDone > 0 && <div style={{ width: `${(nDone / tTotal) * 100}%`, background: 'hsl(var(--success))' }} />}
              {nOpen > 0 && <div style={{ width: `${(nOpen / tTotal) * 100}%`, background: 'hsl(var(--primary))' }} />}
              {nOver > 0 && <div style={{ width: `${(nOver / tTotal) * 100}%`, background: 'hsl(var(--danger))' }} />}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[11.5px] text-[hsl(var(--foreground-muted))] flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm inline-block" style={{ background: 'hsl(var(--success))' }} />{nDone} done</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm inline-block" style={{ background: 'hsl(var(--primary))' }} />{nOpen} open</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm inline-block" style={{ background: 'hsl(var(--danger))' }} />{nOver} overdue</span>
            </div>
          </>
        )}
      </div>
      <div className="card p-4">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">
          Projects by stage
        </div>
        <MiniBars items={stageItems} testId="home-stage-bars" />
      </div>
    </div>
  );
}
